import { useRef, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { SelectGeneric, Switch, Button } from 'ypf-pas-general-ui-components';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import * as yup from 'yup';

import { getEventsWellTeam, getEventWellAsociatedWithWell } from '../../../services/yclickMaster';
import useToast from '../../../hooks/useToast';
import AccountAssignmentObjectForm from '../AccountAssignmentObjectForm/AccountAssignmentObjectForm';
import './AddWellForm.scss';
import { checkRol } from '../../../security';
import { proveedorNotificador } from '../../../security/constantsRols';

const AddWellForm = ({
  wells,
  params,
  handleAddWell,
  onCloseForm,
  setLoading,
  eventId: idEvent,
  imputationObjectDefault,
  typeOfEventId,
  createSDS,
  fromPteAuthorization
}) => {
  const initialValues = {
    well: null,
    event: null,
    team: null,
    viewAllTeams: false,
    imputationType: null,
    imputationObject: null
  };
  const { optionsImputationType } = params;
  const [events, setEvents] = useState([]);
  const [teams, setTeams] = useState([]);
  const { setNewToast } = useToast();
  const wellFormRef = useRef();

  const getTeamsByEvent = async (eventWellId, viewAllTeams) => {
    try {
      const teamsResponse = await getEventsWellTeam(eventWellId, viewAllTeams);

      const teamOptions = (teamsResponse?.opciones || []).map((t) => ({
        ...t,
        value: t?.id,
        label: t?.nombre
      }));

      setTeams(teamOptions);

      let selectedTeam = null;

      if (teamsResponse?.default) {
        selectedTeam = {
          ...teamsResponse.default,
          value: teamsResponse.default.id,
          label: teamsResponse.default.nombre
        };
      } else {
        selectedTeam = teamOptions[0] || null;
      }

      wellFormRef.current.setFieldValue('team', selectedTeam);
      wellFormRef.current.setFieldTouched('team');
    } catch (err) {
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
      setTeams([]);
    }
  };

  // const getEventsByWell = async (wellId) => {
  //   try {
  //     const eventResponse = await getEventsWell(wellId);

  //     setEvents(eventResponse?.opciones);
  //     if (eventResponse.default) {
  //       wellFormRef.current.setFieldValue('event', {
  //         ...eventResponse.default,
  //         value: eventResponse.default.id,
  //         label: eventResponse.default.nombre
  //       });
  //       getTeamsByEvent(eventResponse.default.eventoPozoId);
  //     } else {
  //       wellFormRef.current.setFieldValue('event', null);
  //     }
  //   } catch (err) {
  //     setNewToast({
  //       type: 'ERROR',
  //       message: err.message
  //     });
  //     setEvents([]);
  //     setTeams([]);
  //   }
  // };

  const getEventOptions = async (id, typeEventId) => {
    try {
      const res = await getEventWellAsociatedWithWell(id, typeEventId);

      const eventsOpt = res?.opciones?.map((item) => ({
        ...item,
        label: item?.nombre,
        value: item?.id
      }));

      setEvents(eventsOpt);
      if (res?.default) {
        wellFormRef.current.setFieldValue('event', {
          ...res.default,
          value: res.default.id,
          label: res.default.nombre
        });
        getTeamsByEvent(res.default.eventoPozoId, wellFormRef.current.values.viewAllTeams);
      } else {
        wellFormRef.current.setFieldValue('event', null);
      }
    } catch (error) {
      setNewToast({
        type: 'ERROR',
        message: error.message
      });
    }
  };

  const handleChangeObject = (values) => {
    wellFormRef.current.setValues({
      ...wellFormRef.current.values,
      ...values
    });
  };

  const schemaValidation = yup.object().shape({
    well: yup.object().required('Debe seleccionar un pozo').nullable(),
    event: yup
      .object()
      .test(
        'diferentEvent',
        'Ingrese el mismo evento para todos los pozos',
        (eventSelected, data) => {
          let error = true;
          const wellSelected = data?.from[1]?.value?.well;
          const eventId = wells.length ? wells[0].event?.id : idEvent;
          const filteredWells = wells?.filter((well) => !well?.eliminado);

          if (!filteredWells?.length) return true;

          if (!wellSelected) return error;
          if (eventId && eventSelected && eventSelected?.id !== eventId) {
            error = false;
          }

          // The id belongs to the event
          // map so that value is now wellEventId
          // (which is specific to the Event–Well relationship)
          // wellEventId and event id are always different
          return error;
        }
      )
      .required('Debe seleccionar un evento')
      .nullable(),
    team: yup.object().required('Debe seleccionar un equipo').nullable(),
    // imputationType: yup.object().required('Must select imputation type').nullable(),
    // imputationObject: yup.object().required('Must select imputation object').nullable(),
    imputationType: yup
      .object()
      .test('conditionalImputationType', 'Debe seleccionar tipo imputación', (row, data) => {
        const imputationTypeValue = data?.from[1]?.value?.imputationType;

        if (imputationTypeValue) return true;

        return false;
      })
      .nullable(),
    imputationObject: yup
      .object()
      .test('conditionalImputationObject', 'Debe seleccionar objeto imputación', (row, data) => {
        const imputationObjectValue = data?.from[1]?.value?.imputationObject;

        if (imputationObjectValue) return true;

        return false;
      })
      .nullable()
  });

  return (
    <div>
      <h6>Ubicación</h6>
      <div className="add-well">
        <div className="my-1">
          <Formik
            initialValues={initialValues}
            innerRef={wellFormRef}
            validationSchema={schemaValidation}
          >
            {({
              values,
              setFieldValue,
              errors,
              setFieldTouched,
              resetForm,
              isValid,
              dirty,
              touched
            }) => {
              const handleWellChange = async (newValue) => {
                setFieldValue('well', newValue);
                setFieldTouched('well');
                setEvents([]);
                setTeams([]);
                setFieldValue('event', null);
                setFieldValue('team', null);
                setFieldValue('imputationObject', null);
                setFieldValue('imputationType', null);
                if (newValue) {
                  getEventOptions(newValue.id, typeOfEventId);
                }
              };

              const handleEventChange = (newValue) => {
                setFieldValue('event', newValue);
                setFieldTouched('event');
                setFieldValue('team', null);
                setFieldValue('imputationObject', null);
                setFieldValue('imputationType', null);

                getTeamsByEvent(newValue.eventoPozoId, values.viewAllTeams);
              };

              const handleTeamChange = async (newValue) => {
                setFieldValue('team', newValue);
                setFieldTouched('team');
              };
              const handleUnitSwitchChange = async () => {
                setFieldValue('viewAllTeams', !values.viewAllTeams);
                setFieldValue('team', null);
                getTeamsByEvent(values.event.eventoPozoId, !values.viewAllTeams);
              };

              return (
                <>
                  <Form>
                    <Row className="d-flex align-items-center">
                      <Col sm={3}>
                        <SelectGeneric
                          errors={errors.well}
                          isInvalid={errors.well && touched.well}
                          label="Pozo *"
                          name="well"
                          onChange={handleWellChange}
                          optionMapper={{
                            value: 'id',
                            label: 'nombre'
                          }}
                          options={params?.optionsWell}
                          paddingIndicator={4}
                          placeholder="Selecciona una ubicación"
                          value={values.well}
                          withSelect="100%"
                        />
                      </Col>
                      <Col sm={3}>
                        <SelectGeneric
                          errors={errors.event}
                          isDisabled={!values.well}
                          isInvalid={errors.event && touched.event}
                          label="Evento *"
                          name="event"
                          onChange={handleEventChange}
                          optionMapper={{
                            value: 'eventoPozoId',
                            label: 'nombre'
                          }}
                          options={events}
                          paddingIndicator={4}
                          placeholder="Selecciona un evento"
                          value={values.event}
                          withSelect="100%"
                        />
                      </Col>

                      <Col sm={3}>
                        <SelectGeneric
                          isDisabled={!values.event}
                          label="Equipo *"
                          name="team"
                          onChange={handleTeamChange}
                          options={teams}
                          paddingIndicator={4}
                          placeholder="Selecciona un equipo"
                          value={values.team}
                          // errors={errors.team}
                          // isInvalid={errors.team && touched.team}
                          withSelect="100%"
                        />
                      </Col>
                      <Col sm={2}>
                        <div className="switch-teams">
                          <h6>Ver todos los equipos</h6>
                          <Switch
                            checked={values.viewAllTeams}
                            disabled={!values.event}
                            falseLabel="NO"
                            name="businessUnitSwitch"
                            onChange={handleUnitSwitchChange}
                            size="sm"
                            trueLabel="SÍ"
                          />
                        </div>
                      </Col>
                    </Row>
                  </Form>
                  <AccountAssignmentObjectForm
                    disabledForm={
                      !values.well ||
                      !values.event ||
                      (imputationObjectDefault && !createSDS) ||
                      checkRol(proveedorNotificador)
                    }
                    eventId={values.event?.id}
                    eventPozoId={values.event?.eventoPozoId}
                    fromPteAuthorization={fromPteAuthorization}
                    handleChangeForm={handleChangeObject}
                    imputationObjectDefault={imputationObjectDefault}
                    imputationTypeOptions={optionsImputationType}
                    setLoading={setLoading}
                    wellId={values.well?.value || 0}
                  />
                  <div className="d-flex justify-content-end">
                    <Button
                      className="me-2"
                      label="Cancelar"
                      onClick={() => {
                        resetForm();
                        onCloseForm();
                      }}
                      type="button"
                      variant="outline-primary"
                    />
                    <Button
                      disabled={!!Object.keys(errors).length || !isValid || !dirty}
                      label="Agregar"
                      onClick={() => {
                        handleAddWell(values);
                        resetForm();
                        onCloseForm();
                      }}
                      type="button"
                      variant="primary"
                    />
                  </div>
                </>
              );
            }}
          </Formik>
        </div>
      </div>
    </div>
  );
};

AddWellForm.propTypes = {
  wells: PropTypes.arrayOf(
    PropTypes.shape({
      event: PropTypes.shape({
        id: PropTypes.number,
        value: PropTypes.number
      })
    })
  ),
  params: PropTypes.shape({
    optionsImputationType: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        value: PropTypes.number
      })
    ),
    optionsWell: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        value: PropTypes.number
      })
    )
  }).isRequired,
  handleAddWell: PropTypes.func,
  onCloseForm: PropTypes.func,
  setLoading: PropTypes.func,
  eventId: PropTypes.number,
  imputationObjectDefault: PropTypes.bool,
  typeOfEventId: PropTypes.number.isRequired,
  createSDS: PropTypes.bool,
  fromPteAuthorization: PropTypes.bool
};

AddWellForm.defaultProps = {
  wells: [],
  handleAddWell: () => {},
  onCloseForm: () => {},
  setLoading: () => {},
  eventId: null,
  imputationObjectDefault: false,
  createSDS: false,
  fromPteAuthorization: false
};

export default AddWellForm;
